import React, { useCallback, use, useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router';
import classNames from 'classnames';
import { FilterType } from '../Filter';
import {
    makeEntriesLinkLocation,
    useEntriesParams,
    useForceReload,
} from '../helpers/uri';
import { Collapse } from '@kunukn/react-collapse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as icons from '../icons';
import { LocalizationContext } from '../helpers/i18n';

type NavFiltersProps = {
    setNavExpanded: React.Dispatch<React.SetStateAction<boolean>>;
    offlineState: boolean;
    allItemsCount: number;
    allItemsOfflineCount: number;
    unreadItemsCount: number;
    unreadItemsOfflineCount: number;
    starredItemsCount: number;
    starredItemsOfflineCount: number;
};

export default function NavFilters(props: NavFiltersProps): React.JSX.Element {
    const {
        setNavExpanded,
        offlineState,
        allItemsCount,
        allItemsOfflineCount,
        unreadItemsCount,
        unreadItemsOfflineCount,
        starredItemsCount,
        starredItemsOfflineCount,
    } = props;

    const [expanded, setExpanded] = useState(true);

    const params = useEntriesParams();
    const location = useLocation();

    const toggleExpanded = useCallback(
        () => setExpanded((expanded) => !expanded),
        [],
    );

    const collapseNav = useCallback(
        () => setNavExpanded(false),
        [setNavExpanded],
    );

    const newestLink = useMemo(
        () =>
            makeEntriesLinkLocation(location, {
                filter: FilterType.NEWEST,
                id: null,
            }),
        [location],
    );

    const unreadLink = useMemo(
        () =>
            makeEntriesLinkLocation(location, {
                filter: FilterType.UNREAD,
                id: null,
            }),
        [location],
    );

    const starredLink = useMemo(
        () =>
            makeEntriesLinkLocation(location, {
                filter: FilterType.STARRED,
                id: null,
            }),
        [location],
    );

    const forceReload = useForceReload();

    const _ = use(LocalizationContext);

    return (
        <div id="nav-filter-wrapper">
            <h2>
                <button
                    type="button"
                    id="nav-filter-title"
                    className={classNames({
                        'nav-section-toggle': true,
                        'nav-filter-collapsed': !expanded,
                        'nav-filter-expanded': expanded,
                    })}
                    aria-expanded={expanded}
                    onClick={toggleExpanded}
                >
                    <FontAwesomeIcon
                        icon={
                            expanded
                                ? icons.arrowExpanded
                                : icons.arrowCollapsed
                        }
                        size="lg"
                        fixedWidth
                    />{' '}
                    {_('filter')}
                </button>
            </h2>
            <Collapse isOpen={expanded} className="collapse-css-transition">
                <ul id="nav-filter" aria-labelledby="nav-filter-title">
                    <li>
                        <Link
                            id="nav-filter-newest"
                            to={newestLink}
                            state={forceReload}
                            className={classNames({
                                'nav-filter-newest': true,
                                active: params?.filter === FilterType.NEWEST,
                            })}
                            onClick={collapseNav}
                        >
                            {_('newest')}
                            <span
                                className={classNames({
                                    'offline-count': true,
                                    offline: offlineState,
                                    online: !offlineState,
                                    diff:
                                        allItemsCount !==
                                            allItemsOfflineCount &&
                                        allItemsOfflineCount,
                                })}
                                title={_('offline_count')}
                            >
                                {allItemsOfflineCount > 0
                                    ? allItemsOfflineCount
                                    : ''}
                            </span>
                            <span className="count" title={_('online_count')}>
                                {allItemsCount > 0 ? allItemsCount : ''}
                            </span>
                        </Link>
                    </li>
                    <li>
                        <Link
                            id="nav-filter-unread"
                            to={unreadLink}
                            state={forceReload}
                            className={classNames({
                                'nav-filter-unread': true,
                                active: params?.filter === FilterType.UNREAD,
                            })}
                            onClick={collapseNav}
                        >
                            {_('unread')}
                            <span
                                className={classNames({
                                    'unread-count': true,
                                    offline: offlineState,
                                    online: !offlineState,
                                    unread: unreadItemsCount > 0,
                                })}
                            >
                                <span
                                    className={classNames({
                                        'offline-count': true,
                                        offline: offlineState,
                                        online: !offlineState,
                                        diff:
                                            unreadItemsCount !==
                                                unreadItemsOfflineCount &&
                                            unreadItemsOfflineCount,
                                    })}
                                    title={_('offline_count')}
                                >
                                    {unreadItemsOfflineCount > 0
                                        ? unreadItemsOfflineCount
                                        : ''}
                                </span>
                                <span
                                    className="count"
                                    title={_('online_count')}
                                >
                                    {unreadItemsCount > 0
                                        ? unreadItemsCount
                                        : ''}
                                </span>
                            </span>
                        </Link>
                    </li>
                    <li>
                        <Link
                            id="nav-filter-starred"
                            to={starredLink}
                            state={forceReload}
                            className={classNames({
                                'nav-filter-starred': true,
                                active: params?.filter === FilterType.STARRED,
                            })}
                            onClick={collapseNav}
                        >
                            {_('starred')}
                            <span
                                className={classNames({
                                    'offline-count': true,
                                    offline: offlineState,
                                    online: !offlineState,
                                    diff:
                                        starredItemsCount !==
                                            starredItemsOfflineCount &&
                                        starredItemsOfflineCount,
                                })}
                                title={_('offline_count')}
                            >
                                {starredItemsOfflineCount > 0
                                    ? starredItemsOfflineCount
                                    : ''}
                            </span>
                            <span className="count" title={_('online_count')}>
                                {starredItemsCount > 0 ? starredItemsCount : ''}
                            </span>
                        </Link>
                    </li>
                </ul>
            </Collapse>
        </div>
    );
}
